import type { GooglePriceSnapshot } from './schemas/google-usage';

/** Verified paid API token rates, not invoice amounts or currency conversions. */
export const GOOGLE_PRICE_EVIDENCE = {
  revision: 'google-paid-2026-10-17',
  verifiedAt: '2026-10-17T02:46:29.919Z',
  evidenceUrl: 'https://ai.google.dev/gemini-api/docs/pricing?hl=en',
} as const;

type Rates = GooglePriceSnapshot['input'];
type PriceDefinition = {
  input: Rates;
  cachedInput: Rates | null;
  output: number | null;
  longContext?: { input: Rates; cachedInput: Rates; output: number };
};
const uniform = (n: number): Rates => ({ TEXT: n, IMAGE: n, AUDIO: n, VIDEO: n });
const withAudio = (n: number, audio: number): Rates => ({ ...uniform(n), AUDIO: audio });
/** Exact model IDs only. No prefix matching and no generic chat fallback. */
export const GOOGLE_STANDARD_PRICES: Readonly<Record<string, PriceDefinition>> = {
  'gemini-embedding-2': {
    input: { TEXT: 0.20, IMAGE: 0.45, AUDIO: 6.50, VIDEO: 12.00 },
    cachedInput: null, output: null,
  },
  'gemini-2.5-flash': { input: withAudio(0.30, 1), cachedInput: withAudio(0.03, 0.10), output: 2.50 },
  'gemini-2.5-flash-lite': { input: withAudio(0.10, 0.30), cachedInput: withAudio(0.01, 0.03), output: 0.40 },
  'gemini-2.5-pro': {
    input: uniform(1.25), cachedInput: uniform(0.125), output: 10,
    longContext: { input: uniform(2.50), cachedInput: uniform(0.25), output: 15 },
  },
  'gemini-3-flash-preview': { input: withAudio(0.50, 1), cachedInput: withAudio(0.05, 0.10), output: 3 },
  'gemini-3.1-pro-preview': {
    input: uniform(2), cachedInput: uniform(0.20), output: 12,
    longContext: { input: uniform(4), cachedInput: uniform(0.40), output: 18 },
  },
};

export function resolveGooglePriceSnapshot(
  model: string, promptTokens: number | null, serviceTier: string | null = 'standard',
): GooglePriceSnapshot | null {
  const definition = GOOGLE_STANDARD_PRICES[model];
  if (!definition || (definition.longContext && promptTokens === null)) return null;
  const tier = serviceTier === null || serviceTier === 'unspecified' ? 'standard' : serviceTier;
  if (tier !== 'standard' && tier !== 'flex' && tier !== 'priority') return null;
  if (model === 'gemini-embedding-2' && tier !== 'standard') return null;
  const long = definition.longContext && promptTokens !== null && promptTokens > 200_000;
  const selected = long ? definition.longContext : definition;
  if (!selected) return null;
  // Flex caching retains standard rates; priority is explicitly 1.8x on these models.
  const inputFactor = tier === 'flex' ? 0.5 : tier === 'priority' ? 1.8 : 1;
  const cacheFactor = tier === 'priority' ? 1.8 : 1;
  const scale = (rates: Rates, factor: number): Rates => ({
    TEXT: rates.TEXT * factor, IMAGE: rates.IMAGE * factor,
    AUDIO: rates.AUDIO * factor, VIDEO: rates.VIDEO * factor,
  });
  return {
    schemaVersion: 1, ...GOOGLE_PRICE_EVIDENCE, model, api: 'gemini_developer_v1beta',
    source: 'official_documentation', currency: 'USD', unit: 'per_million_tokens',
    serviceTier: tier, contextTier: definition.longContext ? (long ? 'over_200k' : 'up_to_200k') : 'all',
    input: scale(selected.input, inputFactor),
    cachedInput: selected.cachedInput ? scale(selected.cachedInput, cacheFactor) : null,
    outputTextIncludingThoughts: selected.output === null ? null : selected.output * inputFactor,
  };
}
